import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { schoolDatePeriodsService } from '@/services/schoolDatePeriodsService';
import { useSchool } from '@/contexts/SchoolContext';
import { ROUTES } from '@/utils/constants';

// Rolling timeframes never need period checks
const ROLLING_TIMEFRAMES = ['day', 'week', 'month'];

// Shared cache across all instances to prevent multiple simultaneous checks
const periodCheckCache = {};

/**
 * PeriodUndefinedBadge Component
 * Shows a red warning badge when term/semester is selected but undefined
 */
export default function PeriodUndefinedBadge({ timeframe, forceShow = false }) {
  const { schoolId } = useSchool();
  const [isUndefined, setIsUndefined] = useState(false);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (ROLLING_TIMEFRAMES.includes(timeframe)) {
      setIsUndefined(false);
      setReady(true);
      return;
    }

    if (!schoolId) {
      setReady(true);
      return;
    }

    // Use cached result if already checked
    const cached = periodCheckCache[timeframe];
    if (cached?.checked) {
      setIsUndefined(cached.isUndefined);
      setReady(true);
      return;
    }

    const checkPeriod = async () => {
      try {
        const currentPeriod = await schoolDatePeriodsService.getActivePeriod(schoolId, timeframe);
        const shouldShow = !currentPeriod;

        periodCheckCache[timeframe] = {
          checked: true,
          isUndefined: shouldShow
        };

        setIsUndefined(shouldShow);
        setReady(true);
      } catch (error) {
        console.error('[PeriodUndefinedBadge] Error:', error);
        periodCheckCache[timeframe] = {
          checked: true,
          isUndefined: true
        };
        setIsUndefined(true);
        setReady(true);
      }
    };

    checkPeriod();
  }, [timeframe, schoolId]);

  if (!forceShow && (!ready || !isUndefined)) {
    return null;
  }

  const label = timeframe;

  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        navigate(ROUTES.DATE_RANGES);
      }}
      className="inline-block ml-2 px-2 py-0.5 bg-error/10 text-error text-xs rounded-full whitespace-nowrap cursor-pointer hover:bg-error/20 transition-colors"
      title={`Click to define ${label}s`}
    >
      ✕ {label} undefined
    </span>
  );
}
